import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import type { Student } from '@/lib/types';
import { useProfile } from '@/hooks/useProfile';

export default function Dashboard() {
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [rawError, setRawError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const { profile, loading: profileLoading } = useProfile();

  useEffect(() => {
    if (profileLoading) return;
    let mounted = true;

    async function load() {
      setLoading(true);
      setRawError(null);

      try {
        // 1. 현재 세션의 사용자 정보를 직접 가져와서 화면에 표시
        const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
        if (!mounted) return;

        const session = sessionData?.session;
        const email = session?.user?.email ?? null;
        const uid = session?.user?.id ?? null;
        setUserEmail(email);
        setUserId(uid);

        if (sessionErr) {
          setRawError(`세션 에러: ${sessionErr.message}`);
          setLoading(false);
          return;
        }

        if (!session) {
          setRawError('로그인 세션이 없어요. 다시 로그인해 주세요.');
          setLoading(false);
          return;
        }

        // 2. 학생 목록을 RPC 우회 함수로 조회
        const academyId = profile?.academy_id;
        if (!academyId) {
          setRawError('학원 배정 정보가 없어요. 관리자에게 문의해 주세요.');
          setLoading(false);
          return;
        }

        const { data, error } = await supabase.rpc('get_students_by_academy', {
          p_academy_id: academyId,
        });

        if (!mounted) return;

        if (error) {
          setRawError(`학생 조회 에러: ${error.message}`);
          console.error('학생 조회 실패:', error);
        } else {
          setStudents((data as Student[]) || []);
          console.log('학생 조회 성공:', (data as Student[])?.length ?? 0, '건');
        }
      } catch (err: unknown) {
        if (!mounted) return;
        const msg = err instanceof Error ? err.message : '알 수 없는 오류';
        setRawError(`예외 발생: ${msg}`);
        console.error('load 예외:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [profileLoading, profile]);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {/* 디버깅 상태 표시 — 문제 원인을 바로 알 수 있게 */}
      <div className="rounded-lg border border-primary-200 bg-primary-50 px-4 py-4 text-sm">
        <p className="font-semibold text-primary-900">현재 로그인 상태</p>
        <div className="mt-2 space-y-1 text-primary-800">
          <p>
            <strong>이메일:</strong> {userEmail ?? '없음'}{' '}
            {userEmail?.includes('god2086') && '(운영자 계정)'}
            {userEmail?.includes('gmail') && '(원장 계정)'}
          </p>
          <p>
            <strong>UID:</strong> {userId ?? '없음'}
          </p>
          <p>
            <strong>학생 조회 결과:</strong>{' '}
            {loading ? '불러오는 중...' : `${students.length}명`}
          </p>
        </div>
      </div>

      {/* 에러 메시지가 있으면 눈에 띄게 표시 */}
      {rawError && (
        <div className="rounded-lg border border-accent-300 bg-accent-100 px-4 py-4 text-sm text-accent-800">
          <p className="font-semibold">문제가 발생했어요:</p>
          <p className="mt-1">{rawError}</p>
          <p className="mt-2 text-xs">
            위 에러 메시지를 그대로 알려주시면 정확히 고칠 수 있어요.
          </p>
        </div>
      )}

      {/* 학생 목록 */}
      <div className="rounded-lg border border-background-200 bg-white p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-foreground-950">학생 명단</h2>
          <Link
            to="/students"
            className="text-sm font-semibold text-primary-600 hover:text-primary-700"
          >
            전체 보기 <i className="ri-arrow-right-line ml-1"></i>
          </Link>
        </div>

        {loading ? (
          <div className="flex h-48 items-center justify-center">
            <i className="ri-loader-4-line animate-spin text-2xl text-foreground-500"></i>
          </div>
        ) : students.length === 0 ? (
          <div className="py-10 text-center">
            <p className="text-sm font-medium text-foreground-600">
              학생 데이터가 0건 반환됐어요.
            </p>
            <p className="mt-1 text-xs text-foreground-400">
              위 "현재 로그인 상태"에서 이메일이 원장 계정(gmail)인지 확인해 주세요.
              operator 계정으로 로그인하면 학생이 안 보일 수 있어요.
            </p>
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-background-200 bg-background-50">
                <tr>
                  <th className="px-3 py-2.5 font-semibold text-foreground-600">이름</th>
                  <th className="px-3 py-2.5 font-semibold text-foreground-600">학교</th>
                  <th className="px-3 py-2.5 font-semibold text-foreground-600">학년</th>
                  <th className="px-3 py-2.5 font-semibold text-foreground-600">보호자</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s) => (
                  <tr
                    key={s.id}
                    className="border-b border-background-100 last:border-0 hover:bg-background-50"
                  >
                    <td className="px-3 py-2.5 font-semibold text-foreground-900">
                      {s.name}
                    </td>
                    <td className="px-3 py-2.5 text-foreground-600">
                      {s.school || '-'}
                    </td>
                    <td className="px-3 py-2.5 text-foreground-600">
                      {s.grade || '-'}
                    </td>
                    <td className="px-3 py-2.5 text-foreground-600">
                      {s.parent_name || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 빠른 링크 */}
      <div className="rounded-lg border border-background-200 bg-white p-5">
        <h3 className="text-base font-bold text-foreground-950">바로가기</h3>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { to: '/students', label: '학생 관리', icon: 'ri-user-star-line' },
            { to: '/classes', label: '반 / 수업', icon: 'ri-group-line' },
            { to: '/timetable', label: '시간표', icon: 'ri-calendar-line' },
            { to: '/attendance', label: '출결', icon: 'ri-check-double-line' },
          ].map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:bg-white"
            >
              <i className={`${item.icon} text-primary-600`}></i>
              {item.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}